// LandingContent.jsx
import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { NULL_FUNCTION } from '../resource/defaults.js';
import { getIsOnWebviewApp } from '../resource/getUserAgent.js';
import { getIsInNext } from '../resource/getJsEnvironment.js';

import CustomerServiceButton from '../container/CustomerServiceButton.js';
import Loading, { LoadingType } from '../component/Loading.jsx';
import { notAgeVerifyRegExp } from '../resource/pathRegexList.js';
import { removeLangFromPath } from '../resource/langPath.js';

import DropUpSrc from '../../img/ic-dropup-white.svg';
import DropDownSrc from '../../img/ic-dropdown-white.svg';
import FreeShortSrc from '../../img/ic-landing-short.svg';
import LivestreamSrc from '../../img/ic-landing-live.svg';
import Livestream24hSrc from '../../img/ic-landing-24h.svg';
import landingSrc from '../../img/ic_logo_tealblue.svg';

import LandingCP1Src from '../../img/cp1.png';
import LandingCP2Src from '../../img/cp2.png';
import LandingCP3Src from '../../img/cp3.png';
import LandingCP4Src from '../../img/cp4.png';
import LandingCP5Src from '../../img/cp5.png';

const CPList = [
  LandingCP1Src.src || LandingCP1Src,
  LandingCP2Src.src || LandingCP2Src,
  LandingCP3Src.src || LandingCP3Src,
  LandingCP4Src.src || LandingCP4Src,
  LandingCP5Src.src || LandingCP5Src,
];

import media from '../style/media.js';
import {
  color,
  landingGuestModeButtonGradient,
  landingMobileActionBackdropGradient,
} from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import FadeOut from '../style/FadeOut.js';

import { TranslationNamespace } from '../resource/translationNamespace.js';
import { ButtonId, ModalId } from '../resource/mixpanel.js';

const isOnWebviewApp = getIsOnWebviewApp();
const isInNextjs = getIsInNext();
const shouldUseLocalImageSource = isOnWebviewApp || !isInNextjs;

const trackingPayload = {
  modalId: ModalId.Landing.LandingPage2024Iwin,
};

const ActionButtonGroup = ({
  isLoading = false,
  login = NULL_FUNCTION,
  submitAnonymousLogin = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  return (
    <>
      <LoginRegisterButton
        type="button"
        data-element_id={ButtonId.LoginRegister.ButtonLogin}
        data-tracking_payload={trackingPayload}
        onClick={login}
      >
        {t('button_sign_up_log_in', {
          ns: TranslationNamespace.AUTH,
        })}
      </LoginRegisterButton>
      <GuestModeButton
        type="button"
        data-element_id={ButtonId.LoginRegister.ButtonGuestModeEnter}
        data-tracking_payload={trackingPayload}
        onClick={submitAnonymousLogin}
      >
        <GuestModeButtonContent>
          <GuestModeButtonLabel $isHidden={isLoading}>
            {t('button_enter_as_guest', {
              ns: TranslationNamespace.AUTH,
            })}
          </GuestModeButtonLabel>
          <GuestModeButtonLoading>
            {isLoading && <Loading type={LoadingType.WHITE} size={20} />}
          </GuestModeButtonLoading>
        </GuestModeButtonContent>
      </GuestModeButton>
    </>
  );
};
ActionButtonGroup.propTypes = {
  isLoading: PropTypes.bool,
  login: PropTypes.func,
  submitAnonymousLogin: PropTypes.func,
};

export const LandingContent = ({
  status,
  login = NULL_FUNCTION,
  submitAnonymousLogin = NULL_FUNCTION,
  isAnonymousLoginFetching = false,
  brandname = 'SWAG',
  mainHeaderImage = '',
  section1Image = '',
  section2Image = '',
  section3Image = '',
}) => {
  const { t } = useTranslation();
  const wapperRef = useRef(null);
  const [isGoToTopButtonShow, setIsGoToTopButtonShow] = useState(false);
  const [isLogoShrink, setIsLogoShrink] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const wrapperElm = wapperRef.current;
    let lastVisible = false;
    function scrollFunction() {
      const shouldShow = wrapperElm.scrollTop > 20;
      if (shouldShow !== lastVisible) {
        lastVisible = shouldShow;
        setIsGoToTopButtonShow(shouldShow);
        setIsLogoShrink(shouldShow);
        if (!isInitialized) setIsInitialized(true);
      }
    }
    wrapperElm.addEventListener('scroll', scrollFunction);
    return () => {
      wrapperElm.removeEventListener('scroll', scrollFunction);
    };
  }, [isInitialized]);
  const scrollToTop = () => {
    if (wapperRef.current) {
      wapperRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <Wrapper $status={status} ref={wapperRef}>
      <Container>
        <BackgroundTopGradient />
        <StyledLandingHeaderWrapper>
          <Header>
            <SWAGLogo
              $isLogoShrink={isLogoShrink}
              src={shouldUseLocalImageSource ? landingSrc : '/logo/landing'}
              alt={brandname}
              width={69}
              height={30}
            />
            <HeaderActionsWrapper>
              <ActionButtonGroup
                isLoading={isAnonymousLoginFetching}
                login={login}
                submitAnonymousLogin={submitAnonymousLogin}
              />
            </HeaderActionsWrapper>
          </Header>
        </StyledLandingHeaderWrapper>
        <StyledLandingContent>
          <Main>
            <MainTitleBox>
              <MainTitleSWAG>SWAG.live</MainTitleSWAG>
              <MainTitle>
                {t('iwin_landing_page_title', {
                  ns: TranslationNamespace.GENERAL,
                })}
              </MainTitle>
              <MainSubtitle>
                {t('iwin_landing_page_subtitle', {
                  ns: TranslationNamespace.GENERAL,
                })}
              </MainSubtitle>
            </MainTitleBox>
            <MainImage
              src={mainHeaderImage}
              alt="a sexy woman lying bed"
              width={483}
              height={322}
            />
            <MainInfoBox>
              <MainInfoCard>
                <MainInfoNumber>
                  1K <BluePlus>+</BluePlus>
                </MainInfoNumber>
                <MainInfoText>
                  {t('iwin_landing_page_statistic_1', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </MainInfoText>
              </MainInfoCard>
              <MainInfoCard>
                <MainInfoNumber>
                  10K <BluePlus>+</BluePlus>
                </MainInfoNumber>
                <MainInfoText>
                  {t('iwin_landing_page_statistic_2', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </MainInfoText>
              </MainInfoCard>
              <MainInfoCPStreamersBox>
                <MainInfoCPList>
                  {CPList.map((src, index) => (
                    <CPImage
                      key={src}
                      src={src}
                      alt={`SWAG Creator ${index}`}
                      width={48}
                      height={48}
                    />
                  ))}
                </MainInfoCPList>
                <MainInfoCPStreamersNumber>
                  1k<BluePlus>+</BluePlus>{' '}
                </MainInfoCPStreamersNumber>
                <MainInfoCPStreamersText>
                  {t('iwin_landing_page_statistic_3', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </MainInfoCPStreamersText>
              </MainInfoCPStreamersBox>
            </MainInfoBox>
          </Main>
          <DropDownWrapper>
            <DropDown href="#section-area">
              <img src={DropDownSrc} alt="" width={20} height={20} />
            </DropDown>
          </DropDownWrapper>
          <ContentSection id="section-area">
            <ContentSectionImageBox>
              <ContentSectionImage
                src={section1Image}
                alt="SWAG homepage and livestream page"
                width={500}
                height={480}
              />
            </ContentSectionImageBox>
            <ContentSectionTextBox>
              <ContentSectionTitleBox>
                <ContentSectionTitleIconBox>
                  <ContentSectionTitleIcon
                    src={FreeShortSrc}
                    alt=""
                    width={60}
                    height={60}
                  />
                </ContentSectionTitleIconBox>
                <ContentSectionTitle>
                  {t('iwin_landing_page_section_1_title', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </ContentSectionTitle>
              </ContentSectionTitleBox>
              <ContentSectionTextList>
                <ContentSectionText>
                  {t('iwin_landing_page_section_1_body_1', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </ContentSectionText>
                <ContentSectionText>
                  {t('iwin_landing_page_section_1_body_2', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </ContentSectionText>
                <ContentSectionText>
                  {t('iwin_landing_page_section_1_body_3', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </ContentSectionText>
              </ContentSectionTextList>
            </ContentSectionTextBox>
          </ContentSection>
          <ContentSection>
            <ContentSectionTextBox>
              <ContentSectionTitleBox>
                <ContentSectionTitleIconBox>
                  <ContentSectionTitleIcon
                    src={LivestreamSrc}
                    alt=""
                    width={60}
                    height={60}
                  />
                </ContentSectionTitleIconBox>
                <ContentSectionTitle>
                  {t('iwin_landing_page_section_2_title', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </ContentSectionTitle>
              </ContentSectionTitleBox>
              <ContentSectionTextList>
                <ContentSectionText>
                  {t('iwin_landing_page_section_2_body_1', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </ContentSectionText>
                <ContentSectionText>
                  {t('iwin_landing_page_section_2_body_2', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </ContentSectionText>
                <ContentSectionText>
                  {t('iwin_landing_page_section_2_body_3', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </ContentSectionText>
              </ContentSectionTextList>
            </ContentSectionTextBox>
            <ContentSectionImageBox>
              <ContentSectionImage
                src={section2Image}
                alt="Swag creators grid list"
                width={500}
                height={480}
                loading="lazy"
              />
            </ContentSectionImageBox>
          </ContentSection>
          <ContentSection>
            <ContentSectionImageBox>
              <ContentSectionImage
                src={section3Image}
                alt="Livestream pages"
                width={500}
                height={480}
                loading="lazy"
              />
            </ContentSectionImageBox>
            <ContentSectionTextBox>
              <ContentSectionTitleBox>
                <ContentSectionTitleIconBox>
                  <ContentSectionTitleIcon
                    src={Livestream24hSrc}
                    alt=""
                    width={60}
                    height={60}
                  />
                </ContentSectionTitleIconBox>
                <ContentSectionTitle>
                  {t('iwin_landing_page_section_3_title', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </ContentSectionTitle>
              </ContentSectionTitleBox>
              <ContentSectionTextList>
                <ContentSectionText>
                  {t('iwin_landing_page_section_3_body_1', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </ContentSectionText>
                <ContentSectionText>
                  {t('iwin_landing_page_section_3_body_2', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </ContentSectionText>
                <ContentSectionText>
                  {t('iwin_landing_page_section_3_body_3', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </ContentSectionText>
              </ContentSectionTextList>
            </ContentSectionTextBox>
          </ContentSection>
        </StyledLandingContent>
        <FloatingButtonGroup>
          {/* /age-verify/ already has CustomerServiceButton via FloatingActionButtons route in App.jsx, skip here to avoid duplicate */}
          {notAgeVerifyRegExp.test(
            removeLangFromPath(window.location.pathname)
          ) && (
            <CustomerServiceButtonWrapper
              $isGoToTopButtonShow={isGoToTopButtonShow}
              $isInitialized={isInitialized}
            >
              <CustomerServiceButton />
            </CustomerServiceButtonWrapper>
          )}
          <GoToTopButton
            onClick={scrollToTop}
            type="button"
            title="Go to top"
            $isVisible={isGoToTopButtonShow}
          >
            <img src={DropUpSrc} alt="" width={22} height={22} />
          </GoToTopButton>
          <FooterActionsWrapper>
            <ActionButtonGroup
              isLoading={isAnonymousLoginFetching}
              login={login}
              submitAnonymousLogin={submitAnonymousLogin}
            />
          </FooterActionsWrapper>
        </FloatingButtonGroup>
      </Container>
    </Wrapper>
  );
};

LandingContent.propTypes = {
  status: PropTypes.string,
  isAnonymousLoginFetching: PropTypes.bool,
  login: PropTypes.func,
  submitAnonymousLogin: PropTypes.func,
  brandname: PropTypes.string,
  mainHeaderImage: PropTypes.string,
  section1Image: PropTypes.string,
  section2Image: PropTypes.string,
  section3Image: PropTypes.string,
};

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  background-color: ${color.black};
  &::-webkit-scrollbar {
    display: none;
  }
  animation: 0.6s forwards
    ${({ status }) => ('closing' === status ? FadeOut : null)};
`;

const Container = styled.div`
  position: relative;
`;

const BackgroundTopGradient = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  background: linear-gradient(180deg, #191919 0%, #191919 28.5%, #8f1c58 100%);
  z-index: -1;
`;
const StyledLandingHeaderWrapper = styled.div`
  margin-top: 28px;
  background-color: #1a1a1a;
  z-index: 2;
  position: sticky;
  top: 0;
  width: 100%;
  margin-bottom: 48px;
  padding: 12px 0px;
  ${media.tablet`
    margin-top: 0px;
    margin-bottom: 20px;
    padding: 20px 32px;
    width: auto;
    max-width: none;
  `}
  ${media.mobile`
    position: relative;
    padding: 16px;
    margin-bottom: 8px;
    width: auto;
    max-width: none;
  `}
`;

const StyledLandingContent = styled.div`
  position: relative;
  padding-bottom: 20px;
  width: 1024px;
  max-width: 1024px;
  margin: auto;
  ${media.tablet`
    padding: 0 32px 20px;
    width: auto;
    max-width: none;
  `}
  ${media.mobile`
    padding: 0px 16px 16px;
    width: auto;
    max-width: none;
    padding-bottom: 150px
  `}
`;

const Header = styled.header`
  display: flex;
  max-width: 1024px;
  margin: auto;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const SWAGLogo = styled.img`
  width: ${({ $isLogoShrink }) => ($isLogoShrink ? '73px' : '148px')};
  height: ${({ $isLogoShrink }) => ($isLogoShrink ? '32px' : '65px')};
  ${media.tablet`
    width: 69px;
    height: 30px;
  `}
`;

const HeaderActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  ${media.mobile`
    display: none;
  `}
`;

const FooterActionsWrapper = styled.div`
  display: none;
  flex-direction: column-reverse;
  gap: 8px;
  ${media.mobile`
    padding: 70px 16px 32px;
    display: flex;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    background: ${landingMobileActionBackdropGradient}
  `}
`;

const ActionButton = styled.button`
  ${ResetButtonStyle}
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  border: 1px solid white;
  color: #fff;
  font-size: 14px;
  line-height: 150%;
  font-weight: 400;
  ${media.mobile`
    padding: 8px 8px;
    font-size: 16px;
  `}
`;

// Increase padding to compensate for the missing border, keeping the button visually aligned with bordered buttons.
const GuestModeButton = styled(ActionButton)`
  padding: 5px 8px;
  background: ${landingGuestModeButtonGradient};
  border: none;
  ${media.mobile`
    padding: 9px 13px;
    font-size: 16px;
  `}
`;

const GuestModeButtonContent = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const GuestModeButtonLabel = styled.span`
  visibility: ${({ $isHidden }) => ($isHidden ? 'hidden' : 'visible')};
`;

const GuestModeButtonLoading = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
`;

const LoginRegisterButton = styled(ActionButton)`
  ${media.mobile`
   border: 1px solid ${color.neutral[20]};
`}
`;

const Main = styled.section`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 48px;

  & .main-watch-now-button {
    display: none;
  }

  ${media.tablet`
    margin-bottom: 24px;
    gap: 16px;
  `}
  ${media.mobile`
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 328px;
    margin-left: auto;
    margin-right: auto;

    & .main-watch-now-button {
      display: flex;
    }
  `}
`;

const MainTitleBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 12px;
  max-width: 248px;
  ${media.tablet`
    max-width: 184px;
    gap: 6px;
  `}
  ${media.mobile`
    max-width: 100%;
    order: 2;
    gap: 4px;

    & .main-title-box-watch-now-button {
      display: none;
    }
  `}
`;

const MainTitleSWAG = styled.div`
  font-size: 32px;
  font-style: normal;
  font-weight: 510;
  color: ${color.tealBlue};
  ${media.tablet`
    font-size: 20px;
  `}
  ${media.mobile`
    font-weight: 400;
  `}
`;

const MainTitle = styled.h1`
  font-size: 60px;
  font-weight: 1000;
  line-height: 110%;
  margin: 0;
  padding: 0;
  ${media.tablet`
    font-size: 42px;
    font-weight: 700;
  `}
  ${media.mobile`
    font-size: 40px;
  `}
`;

const MainSubtitle = styled.h2`
  font-size: 20px;
  font-weight: 274;
  line-height: 150%;
  letter-spacing: -0.528px;
  margin: 0;
  padding: 0;
  ${media.tablet`
    font-size: 14px;
    letter-spacing: -0.308px;
  `}
  ${media.mobile`
    font-size: 13px;
    letter-spacing: -0.286px;
  `}
`;

const MainImage = styled.img`
  width: auto;
  height: 322px;
  border-radius: 20px;
  ${media.tablet`
    width: auto;
    height: 218px;
  `}
  ${media.mobile`
    width: 100%;
    min-height: 176px;
    height: auto;
    order: 1;
  `}
`;

const MainInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  ${media.tablet`
    gap: 4px;
  `}
  ${media.mobile`
    gap: 8px;
    order: 3;
    flex-direction: row;
  `}
`;

const MainInfoCard = styled.div`
  display: flex;
  width: 157px;
  height: 100px;
  padding: 13px 10px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  background: #161616;
  ${media.tablet`
    width: 83px;
    height: 53px;
    padding: 7px 5px;
    gap: 4px;
    border-radius: 5px;
  `}
  ${media.mobile`
    padding: 8px;
    gap: 8px;
    border-radius: 10px;
  `}
`;

const MainInfoNumber = styled.div`
  color: #d9d9d9;
  font-size: 32px;
  font-weight: 1000;
  ${media.tablet`
    font-size: 18px;
  `}
  ${media.mobile`
    font-size: 16px;
  `}
`;

const MainInfoText = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #5e5e5e;
  ${media.tablet`
    font-size: 8px;
  `}
  ${media.mobile`
    font-size: 10px;
  `}
`;

const MainInfoCPStreamersBox = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: auto auto;
  margin-top: 12px;
  gap: 8px;
  ${media.tablet`
    margin-top: 8px;
    gap: 4px;
  `}
  ${media.mobile`
    margin-top: 0px;
  `}
`;

const MainInfoCPList = styled.div`
  grid-column: 1/3;
  ${media.mobile`
    grid-column: 1/2;
  `}
`;

const BluePlus = styled.span`
  color: ${color.tealBlue};
`;

const CPImage = styled.img`
  margin-left: -24px;
  :first-child {
    margin-left: 0;
  }
  border-radius: 50%;
  border: 2px solid transparent;
  background: linear-gradient(#a445b3 4%, #d41872 54%, #ff0066) border-box;
  ${media.tablet`
    width: 26px;
    height: 26px;
    margin-left: -13px;
  `}
`;

const MainInfoCPStreamersNumber = styled.div`
  font-size: 24px;
  font-weight: 1000;
  background: linear-gradient(
    136deg,
    #a445b3 3.79%,
    #d41872 48.03%,
    #f06 97.19%
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  ${media.tablet`
    font-size: 16px;
  `}
`;

const MainInfoCPStreamersText = styled.div`
  color: #dedede;
  font-size: 20px;
  font-weight: 400;
  ${media.tablet`
    font-size: 10px;
  `}
  ${media.mobile`
    font-size: 12px;
    grid-column: 1/3;
  `}
`;

const DropDownWrapper = styled.div`
  margin-bottom: 48px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  ${media.tablet`
    margin-bottom: 24px;
  `}
  ${media.mobile`
    > {
      width: 14px;
      height: 14px;
    }
  `}
`;

const DropDown = styled.a``;

const ContentSection = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  gap: 12px;
  ${media.tablet`
    gap: 12px;
    gap: 34px;
    margin-bottom: 7px;
  `}
  ${media.mobile`
    font-size: 12px;
    grid-column: 1/3;
    margin-bottom: 24px;
    flex-direction: column;
    gap: 12px;
  `}
`;

const ContentSectionTitleIconBox = styled.div`
  position: relative;
  ${media.tablet`
    width: 40px;
    height: 40px;
  `}
  ${media.mobile`
    width: 30px;
    height: 30px;
  `}
`;

const ContentSectionTitleIcon = styled.img`
  ${media.tablet`
    width: 40px;
    height: 40px;
  `}
  ${media.mobile`
    width: 30px;
    height: 30px;
  `}
`;

const ContentSectionImage = styled.img`
  ${media.tablet`
    width: 340px;
    height: 330px;
  `}
  ${media.mobile`
    width: 328px;
    height: 310px;
  `}
`;

const ContentSectionImageBox = styled.div`
  ${media.mobile`
    order: 1;
  `}
`;

const ContentSectionTextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${media.tablet`
    gap: 14px;
  `}
  ${media.mobile`
    gap: 8px;
    order: 2;
  `}
`;

const ContentSectionTitleBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

const ContentSectionTitle = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 36px;
  font-weight: 1000;
  line-height: 110%;
  ${media.tablet`
    font-size: 24px;
  `}
`;

const ContentSectionTextList = styled.ul`
  margin: 0;
  ${media.mobile`
    gap: 8px;
    list-style: none;
    padding: 0;
  `}
`;

const ContentSectionText = styled.li`
  color: #d9d9d9;
  font-size: 24px;
  font-weight: 400;
  line-height: 36px; /* 150% */
  letter-spacing: 2px;
  ${media.tablet`
    font-size: 16px;
    line-height: 24px; /* 150% */
    letter-spacing: 1.154px;
  `}
  ${media.mobile`
    text-align: center;
  `}
`;

const FloatingButtonGroup = styled.div`
  position: sticky;
  top: 0;
  left: 0px;
  bottom: 0px;
  width: 100%;
  height: 0;
  z-index: 10;
  max-width: 1024px;
  margin: auto;
`;

const CustomerServiceButtonWrapper = styled.div`
  position: absolute;
  z-index: 2;
  bottom: 32px;
  right: 0;
  transform: translateY(
    ${({ $isGoToTopButtonShow }) => ($isGoToTopButtonShow ? '-60px' : '0')}
  );
  transition: ${({ $isInitialized, $isGoToTopButtonShow }) =>
    $isInitialized
      ? `transform 300ms ease-out ${$isGoToTopButtonShow ? '0ms' : '300ms'}`
      : 'none'};
  ${media.tablet`
     right: 32px;
  `}
  ${media.mobile`
     bottom: 140px;
     right: 16px;
  `}
`;

const GoToTopButton = styled.button`
  ${ResetButtonStyle}
  position: absolute;
  z-index: 2;
  bottom: 32px;
  right: 6px;
  background-color: rgba(71, 71, 71, 0.8);
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
  transition: opacity 300ms ease-out
    ${({ $isVisible }) => ($isVisible ? '300ms' : '0ms')};
  ${media.tablet`
      right: 38px;
  `}
  ${media.mobile`
     bottom: 140px;
     right: 22px;
  `}
`;

export default LandingContent;
